import { api } from './client';
import { useAuthStore } from '@/stores/auth';
import { chatSocketUrl } from '@/lib/chatSocketUrl';
import { teacherNameOf } from '@/lib/studentFields';

export type ChatRole = 'student' | 'curator' | 'admin';

export type MessageType = 'text' | 'image' | 'file' | 'audio';

export type ChatMessage = {
  id: string;
  room_id: string;
  sender_id: string;
  sender_role: ChatRole;
  msg_type?: MessageType;
  content: string;
  attachments?: Attachment[];
  is_read: boolean;
  created_at: string;
  /** Есть только в эхо своего сообщения по сокету: метка, с которой оно уходило. В базе её нет. */
  client_id?: string;
};

export type Attachment = {
  id?: string;
  url: string;
  type: 'image' | 'file' | 'audio';
  name?: string;
};

// Ephemeral realtime events delivered over the chat WebSocket (not persisted).
export type RealtimeEvent =
  | { type: 'typing'; room_id: string; user_id: string; role?: ChatRole; is_typing: boolean }
  | { type: 'presence'; user_id: string; role?: ChatRole; online: boolean };

export type ChatSession = {
  id: string; // == room_id
  student_id: string;
  /**
   * У ученика служба переименует в `teacher_id` (договор student-api.md §4),
   * поэтому поле необязательное. Ученику читать через `teacherIdOf`.
   */
  curator_id?: string;
  teacher_id?: string;
  // Enriched display names (backend enrichNames); prefer these over raw UUIDs.
  student_name?: string;
  /**
   * Имя и фото куратора. У ученика служба переименует в `teacher_name` и `teacher_avatar_url`
   * (заход «1б», договор student-api.md §4). Ученику читать через `teacherNameOf` / `teacherAvatarOf`.
   * Фото пустое — рисуем буквы, это нормальное состояние.
   */
  curator_name?: string;
  curator_avatar_url?: string;
  teacher_name?: string;
  teacher_avatar_url?: string;
  // Enriched for the curator's list: the student's marathon + group (led by this
  // curator) so the UI shows "Марафон | Группа" and can filter by group.
  student_marathon?: string;
  student_group?: string;
  student_group_id?: string;
  type: 'direct' | 'support' | 'group';
  status: 'active' | 'closed';
  last_message?: ChatMessage;
  unread_count?: number;
};

/**
 * Конверт ответа: сервер отдаёт либо `items`, либо `messages`, либо голый
 * массив. Раньше это место было размечено как `any` — тип потерялся, и
 * ошибка в имени поля не всплыла бы до времени выполнения.
 */
type Envelope<T> = { items?: T[]; messages?: T[] };

/**
 * Имя куратора ученика из списка его переписок.
 *
 * ⚠️ БРАТЬ ПЕРВУЮ ИЗ СПИСКА НЕЛЬЗЯ. 02.09.2026 на экране «Учёба» показывалось
 * одно имя, а в уроке — другое: у ученика бывает несколько переписок (личная с
 * куратором, поддержка, групповая), и порядок их прихода с сервера ничем не
 * закреплён. Берём именно ЛИЧНУЮ и действующую.
 */
export function curatorNameOf(sessions?: ChatSession[]): string | undefined {
  return teacherNameOf(curatorOf(sessions));
}

/** Диалог с куратором — из него берём и имя, и фото. */
export function curatorOf(sessions?: ChatSession[]): ChatSession | undefined {
  // Пустые элементы пропускаем: `[null]` в списке ронял экран чата на `c.type` (03.10.2026).
  const real = (sessions ?? []).filter((c): c is ChatSession => Boolean(c));
  const direct = real.find((c) => c.type === 'direct' && c.status === 'active');
  return direct ?? real[0];
}

export const chatApi = {
  // ----- REST -----
  async listMyChats(role: ChatRole = 'student') {
    if (role === 'curator') {
      // Backend returns an array of sessions (client aliases it to .items).
      const { data } = await api.get('/curator/chats');
      return ((data as { items?: ChatSession[] }).items ?? data ?? []) as ChatSession[];
    }
    // Student endpoint returns a SINGLE session object (or null), not an array.
    const { data } = await api.get('/student/chat');
    // ⚠️ «Диалога нет» приходит двумя формами (25.09.2026, договор окна B): `data: null` — и тогда `unwrap`
    // отдаёт null, — или без поля `data` вовсе (`omitempty` в shared/response при нетипизированном nil),
    // и тогда `unwrap` отдаёт саму обёртку `{status, message}`. Без проверки `id` её приняли бы за диалог.
    if (!data || !(data as Partial<ChatSession>).id) return [] as ChatSession[];
    return [data as ChatSession];
  },
  // Find-or-create the direct dialog between the student and their curator (the
  // curator is auto-resolved from enrollment when not given). Lets the STUDENT
  // start the conversation instead of waiting for the curator to message first.
  async openStudentChat(curatorId?: string) {
    const { data } = await api.post('/student/chat/open', curatorId ? { curator_id: curatorId } : {});
    return data as ChatSession;
  },
  // Find-or-create the direct dialog between the curator and a given student —
  // lets the CURATOR start the first conversation.
  async openCuratorChat(studentId: string) {
    const { data } = await api.post('/curator/chats/open', { student_id: studentId });
    return data as ChatSession;
  },
  async getHistory(roomId: string, offset = 0, limit = 50) {
    // Backend responds with { messages: [...], limit, offset };
    // client.ts aliases the first array field to `items`.
    const { data } = await api.get(`/chat/${roomId}/messages`, { params: { offset, limit } });
    return ((data as Envelope<ChatMessage>).items ?? (data as Envelope<ChatMessage>).messages ?? []) as ChatMessage[];
  },
  async sendMessage(payload: {
    room_id: string;
    content: string;
    role: ChatRole;
    msg_type?: MessageType;
    attachments?: Attachment[];
  }) {
    // Backend expects sender_id; take it from the authenticated user.
    const senderId = useAuthStore.getState().user?.id;
    const { data } = await api.post('/chat/messages', {
      room_id: payload.room_id,
      sender_id: senderId,
      role: payload.role,
      content: payload.content,
      msg_type: payload.msg_type,
      attachments: payload.attachments,
    });
    return data as ChatMessage;
  },
  async markRead(roomId: string, _messageIds: string[]) {
    // Backend marks all unread messages in a room as read for the given user;
    // it expects { room_id, user_id } rather than explicit message ids.
    const userId = useAuthStore.getState().user?.id;
    await api.post('/chat/messages/read', { room_id: roomId, user_id: userId });
  },
  // Initial presence state for the room counterpart (typing-indicator seed).
  async getPresence(roomId: string) {
    const { data } = await api.get(`/chat/${roomId}/presence`);
    return data as { online: boolean; counterpart_id: string; room_id: string };
  },
  // ----- Admin monitoring (TZ 3.7) -----
  async searchMessages(query: string, limit = 50) {
    // client.ts aliases the first array field (messages) to `items`.
    const { data } = await api.get('/admin/chats/search', { params: { q: query, limit } });
    return ((data as Envelope<ChatMessage>).items ?? (data as Envelope<ChatMessage>).messages ?? []) as ChatMessage[];
  },

  // ----- WebSocket -----
  connectWS(): WebSocket | null {
    const token = useAuthStore.getState().accessToken;
    if (!token) return null;
    // Шлюз на этом пути вход не проверяет и заголовки `X-User-*` обнуляет: chat-service сам проверяет `?token=`
    // (только access-токен, срок — 15 минут, смотрится один раз, на рукопожатии). Адрес — от адреса API, а не
    // страницы: в приложении страница живёт на `localhost` (`lib/chatSocketUrl.ts`).
    return new WebSocket(chatSocketUrl(token));
  },
  // Send a chat message over an open socket (text and/or attachments).
  sendWS(ws: WebSocket, payload: MessageFramePayload) {
    ws.send(messageFrame(payload));
  },
  // Notify the counterpart that we started/stopped typing (ephemeral).
  sendTyping(ws: WebSocket, roomId: string, isTyping: boolean) {
    if (ws.readyState !== WebSocket.OPEN) return;
    ws.send(typingFrame(roomId, isTyping));
  },
};

export type MessageFramePayload = {
  room_id: string;
  content: string;
  msg_type?: MessageType;
  attachments?: Attachment[];
  /** Метка своего неотправленного сообщения: сервер вернёт её в эхо и в отказе (`error.client_id`). */
  client_id?: string;
};

/** Кадр сообщения для сокета. */
export function messageFrame(payload: MessageFramePayload): string {
  return JSON.stringify({ type: 'message', ...payload });
}

/** Кадр «печатает / перестал» — не сохраняется. */
export function typingFrame(roomId: string, isTyping: boolean): string {
  return JSON.stringify({ type: 'typing', room_id: roomId, is_typing: isTyping });
}
